<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'

// Collapses its slot content to `lines` lines and reveals the rest via a
// "Show more" toggle. The toggle only appears when the content actually
// overflows the collapsed height.
const props = withDefaults(defineProps<{
  lines?: number
  moreText?: string
  lessText?: string
}>(), {
  lines: 6,
  moreText: 'Show more',
  lessText: 'Show less',
})

const contentRef = ref<HTMLElement | null>(null)
const expanded = ref(false)
const needsToggle = ref(false)
const collapsedMaxHeight = ref<string>('none')

let observer: ResizeObserver | null = null

const measure = (): void => {
  const el = contentRef.value
  if (!el) return

  const computed = getComputedStyle(el)
  const lineHeight = parseFloat(computed.lineHeight)
  const perLine = Number.isFinite(lineHeight) ? lineHeight : parseFloat(computed.fontSize) * 1.5 || 20
  const maxHeight = perLine * props.lines

  collapsedMaxHeight.value = `${maxHeight}px`
  // scrollHeight is the full (unclamped) content height even while collapsed.
  needsToggle.value = el.scrollHeight > maxHeight + 1
}

const toggle = (): void => {
  expanded.value = !expanded.value
}

onMounted(async () => {
  await nextTick()
  measure()
  if (typeof ResizeObserver !== 'undefined' && contentRef.value) {
    observer = new ResizeObserver(() => measure())
    observer.observe(contentRef.value)
  }
})

onBeforeUnmount(() => {
  observer?.disconnect()
})
</script>

<template>
  <div>
    <div
      ref="contentRef"
      :style="!expanded ? { maxHeight: collapsedMaxHeight, overflow: 'hidden' } : undefined"
    >
      <slot />
    </div>

    <p
      v-if="needsToggle"
      :class="{'show-more-toggle':true, 'cursor-pointer':true, 'mt-3':!expanded}"
      @click="toggle"
    >
      {{ expanded ? lessText : moreText }}
    </p>
  </div>
</template>

<style lang="scss">
.show-more-toggle {
  color: #07bf9b;
  cursor: pointer;
  text-align: center;
  margin-top: 1rem;
  margin-bottom: 0;
}
</style>
